import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Signed URLs for the private `signatures` bucket, one per storage path.
 *
 * A signed URL carries its token in the query string, so every mint is a NEW
 * url and the browser cannot serve the image from cache. Before this the same
 * signature was minted and downloaded for the sign dialog's preview, minted
 * and downloaded again for the document's block the moment the dialog closed,
 * and again for the next document by the same signer. Against a remote
 * Supabase each of those is two round trips in series -- mint, then fetch --
 * which is the pause a user saw between "Sign" and the ink appearing, and
 * between opening the dialog and its preview filling in.
 *
 * One url per path, reused until it is near expiry, de-duplicated while a mint
 * is in flight, and the image is fetched once on mint so every later <img> of
 * it paints from the browser cache.
 *
 * Module-level on purpose: a signature is a file, not a document, and the
 * same person's signature appears on document after document. A url here is
 * no more exposed than it already is as an <img src>, and the cache goes with
 * the page.
 */

const SIGNED_URL_TTL_S = 3600;

// Reuse only while at least this long remains, so a url handed to an <img> a
// moment before it lapses does not fail to load.
const REUSE_MARGIN_MS = 5 * 60 * 1000;

interface CachedUrl {
  url: string;
  expiresAt: number;
}

const cache = new Map<string, CachedUrl>();
const inflight = new Map<string, Promise<string | null>>();

/**
 * Resolve a signed url for `path`. Resolves immediately from cache when one is
 * fresh; otherwise mints one, shares the mint with any concurrent caller, and
 * warms the browser's image cache before resolving.
 */
export const getSignatureUrl = (
  supabase: SupabaseClient,
  path: string
): Promise<string | null> => {
  const hit = cache.get(path);
  if (hit && hit.expiresAt - Date.now() > REUSE_MARGIN_MS) {
    return Promise.resolve(hit.url);
  }

  const pending = inflight.get(path);
  if (pending) return pending;

  const mint = supabase.storage
    .from('signatures')
    .createSignedUrl(path, SIGNED_URL_TTL_S)
    .then(({ data, error }) => {
      inflight.delete(path);
      if (error || !data?.signedUrl) {
        console.error('Error creating signature URL:', path, error);
        return null;
      }
      cache.set(path, {
        url: data.signedUrl,
        expiresAt: Date.now() + SIGNED_URL_TTL_S * 1000,
      });
      // Fetch the bytes now. The <img> that eventually shows this url then
      // paints from cache instead of starting its own download.
      const warm = new Image();
      warm.src = data.signedUrl;
      return data.signedUrl;
    });

  inflight.set(path, mint);
  return mint;
};

/**
 * Warm the cache for a signature that is likely to be shown soon -- the
 * current user's own, and the nominated signatory's -- so that when the sign
 * dialog opens its preview is already loaded.
 */
export const prefetchSignatureUrl = (
  supabase: SupabaseClient,
  path: string | null | undefined
): void => {
  if (path) void getSignatureUrl(supabase, path);
};
